"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShareButton } from "@/components/ui/share-button";
import { QRShareButton } from "@/components/ui/qr-share-button";
import {
  ArrowLeft, Play, Heart, FileVideo, Calendar, Eye, User,
} from "lucide-react";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import React, { useState, useEffect, use } from "react";
import { getVideoById, getLatestVideos, type BBVideo } from "@/services/videos";
import SaveButton from "@/components/save-button";
import VideoPlayer from "@/components/video-player";
import { getThumbnailUrl, extractVideoId } from "@/lib/youtube";
import Image from "next/image";
import ClaimCreditsButton from "@/components/claim-credits-button";
import { artistProfileHref } from "@/lib/artist-link";
import { MediaCreditsPanel } from "@/components/media/media-credits-panel";

export default function VideoDetailPage({ params }: { params: Promise<{ id: string }> }) {
  const resolvedParams = use(params);
  const [video, setVideo] = useState<BBVideo | null>(null);
  const [relatedVideos, setRelatedVideos] = useState<BBVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const data = await getVideoById(resolvedParams.id);
        setVideo(data);
        const related = await getLatestVideos(6);
        setRelatedVideos(related.filter(v => v.videoId !== resolvedParams.id).slice(0, 4));
      } catch (err) { console.error(err); } finally { setIsLoading(false); }
    }
    load();
  }, [resolvedParams.id]);

  const formatDate = (ts: any) => {
    if (!ts) return "";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  if (isLoading) return <div className="min-h-screen bg-background flex items-center justify-center"><FileVideo className="h-12 w-12 text-muted-foreground animate-pulse" /></div>;

  if (!video) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <FileVideo className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Video not found</h2>
        <Link href="/media"><Button variant="outline"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button></Link>
      </div>
    </div>
  );

  const artistHref = artistProfileHref(video.artist, video.artistId);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border/50 bg-background/95 backdrop-blur sticky top-[50px] z-30">
        <div className="w-full px-page py-3 flex items-center gap-3">
          <Breadcrumbs className="min-w-0 flex-1" items={[
            { navKey: 'videos', href: '/videos', icon: FileVideo },
            { label: video.title, icon: FileVideo },
          ]} />
        </div>
      </div>

      <div className="w-full px-page py-6">
        {(video.youtubeId || video.url) && (
          <VideoPlayer videoId={extractVideoId(video.youtubeId || video.url || '') || video.youtubeId || ''} autoplay controls showInfo={false} className="mb-4" />
        )}

        <h1 className="text-2xl font-bold">{video.title}</h1>
        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
          {video.artist?.name && (
            artistHref ? (
              <Link href={artistHref} className="flex items-center gap-1 hover:text-primary transition-colors">
                <User className="h-3.5 w-3.5" />{video.artist.name}
              </Link>
            ) : <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{video.artist.name}</span>
          )}
          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{formatDate(video.createdAt)}</span>
          {video.genre && <Badge variant="outline">{video.genre}</Badge>}
          {video.viewCount !== undefined && <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{video.viewCount.toLocaleString()} views</span>}
        </div>

        <div className="flex gap-2 mt-4">
          <SaveButton
            itemId={video.videoId || resolvedParams.id}
            itemType="video"
            title={video.title || ''}
            subtitle={video.artist?.name || "Breakin' Bread"}
            imageUrl={video.thumbnailUrl || (video.youtubeId ? getThumbnailUrl(video.youtubeId) : '')}
            youtubeId={video.youtubeId || extractVideoId(video.url || '') || ''}
            link={`/videos/${video.videoId || resolvedParams.id}`}
          />
          <ShareButton title={video.title} />
          <QRShareButton title={video.title} imageUrl={video.thumbnailUrl || ""} />
          <ClaimCreditsButton
            mediaId={video.videoId || resolvedParams.id}
            mediaType="video"
            mediaTitle={video.title || ""}
          />
        </div>

        {video.description && (
          <div className="mt-6 p-4 bg-secondary/30 rounded-lg">
            <p className="text-sm text-muted-foreground whitespace-pre-line">{video.description}</p>
          </div>
        )}

        {/* Credits & Claims */}
        <div className="mt-6 pt-4 border-t border-border/50">
          <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">Credits & Splits</h3>
          <MediaCreditsPanel
            mediaId={video.videoId || resolvedParams.id}
            mediaType="video"
            mediaTitle={video.title || ""}
          />
        </div>

        {/* Related Videos */}
        {relatedVideos.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold mb-3">More Videos</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedVideos.map(rv => (
                <Link key={rv.videoId} href={`/videos/${rv.videoId}`} className="group flex gap-3">
                  <div className="relative w-40 aspect-video bg-muted rounded-lg overflow-hidden flex-shrink-0">
                    <Image
                      src={rv.thumbnailUrl || (rv.youtubeId ? getThumbnailUrl(rv.youtubeId) : '')}
                      alt={rv.title}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Play className="h-8 w-8 text-white/80" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">{rv.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{rv.artist?.name}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(rv.createdAt)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
